import { TableV3ColDefs } from 'common/table';
import { CandidateSortField } from 'modules/candidate-profile/tables/sorting/candidateSort';
import { TableV3FilterItem } from 'common/table/types/TableV3';
import { ICellRendererParams } from 'ag-grid-community';
import { RouterLink } from 'common/ui/RouterLink';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import { getCustomFieldColumns } from 'modules/candidate-profile/tables/columns/customFieldColumns';
import { YEARS_RANGE_FILTER_SETTINGS } from 'modules/candidate-profile/tables/filters/universalFilterFields';
import { OrganizationCustomFieldConfig } from 'generated-graphql/hooks/apollo-hooks';
import { TableV3TruncatedList } from 'common/table/components/TableV3TruncatedList';
import { TableV3TruncatedChipList } from 'common/table/components/TableV3TruncatedChipList';
import { SearchableSortDirection } from 'generated-graphql/types';
import { ApolloClient } from '@apollo/client';
import {
  SearchSkillsDocument,
  SearchableSkillSortableFields,
  SearchableSkillFilterInput,
  SearchTagsDocument,
  SearchableTagFilterInput,
  SearchQualificationsDocument,
  SearchableQualificationFilterInput,
  SearchCandidateProfileListsDocument
} from 'generated-graphql/graphql';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { buildTextSearchClauses } from 'utils/buildTextSearchClauses';
import { startsWithLetter } from 'utils/startsWithLetter';
import { logger } from 'core/utils/logger';
import { Typography } from '@mui/material';
import { toPercentage } from 'utils/calculations';
import { CandidateProfileNameCell } from 'modules/candidate-profile/tables/components/CandidateProfileNameCell';
import { CandidateProfileExplanationCell } from 'modules/candidate-profile/tables/components/CandidateProfileExplanationCell';
import { TalentListChipsCell } from 'modules/candidate-profile/tables/components/TalentListChipsCell';
import { AtsCandidateProfileLink } from 'services/integration/components/AtsCandidateProfileLink';
import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';
import { ActionPopover } from 'components/ActionPopover';

type GetCandidateProfileTableColumnsProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apolloClient: ApolloClient<any>;
  candidateProfileListProps: CandidateProfileListProps;
  vectorDbTenantName: string;
  integrationProvider: string;
  isAtsConnected: boolean;
  hasAnyExternalUrl: boolean;
  organizationId: string;
  onRemoveFromList?: (candidateProfileId: string) => void;
  hasAnyExplanation: boolean;
  hasAnyScore: boolean;
  customFieldConfigs: OrganizationCustomFieldConfig[];
};

// The option queries default to 10, which silently drops matches: searching
// "Ma" returned ten alphabetically and left out a skill sitting in the table.
const FILTER_OPTION_LIMIT = 50;

const UNTYPED_SAMPLE_LIMIT = 200;

export const getCandidateProfileTableColumns = ({
  apolloClient,
  candidateProfileListProps,
  vectorDbTenantName,
  integrationProvider,
  isAtsConnected,
  hasAnyExternalUrl,
  organizationId,
  onRemoveFromList,
  hasAnyExplanation,
  hasAnyScore,
  customFieldConfigs
}: GetCandidateProfileTableColumnsProps): TableV3ColDefs<
  CandidateProfileTableColumns,
  CandidateSortField
> => {
  const showRemoveFromList =
    candidateProfileListProps.type ===
    CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL;

  return [
    {
      field: 'score',
      headerName: 'Score',
      width: 120,
      sortField: 'score',
      hide: !hasAnyScore,
      cellRenderer: (
        params: ICellRendererParams<CandidateProfileTableColumns>
      ) => {
        const score = params.data?.score;

        if (score === null || score === undefined) {
          return <Typography>-</Typography>;
        }

        return <Typography>{toPercentage(score)}</Typography>;
      }
    },
    {
      field: 'explanation',
      headerName: 'Explanation',
      width: 360,
      minWidth: 240,
      hide: !hasAnyExplanation,
      cellRenderer: (
        params: ICellRendererParams<CandidateProfileTableColumns>
      ) => {
        return (
          <CandidateProfileExplanationCell
            explanation={params.data?.explanation ?? null}
            candidateProfileId={params.data?.id}
          />
        );
      }
    },
    {
      field: 'name',
      width: 280,
      searchable: true,
      sortField: 'fullName',
      cellRenderer: (
        params: ICellRendererParams<CandidateProfileTableColumns>
      ) => {
        if (!params.data) {
          return null;
        }

        return (
          <CandidateProfileNameCell
            candidateProfileListProps={candidateProfileListProps}
            id={params.data.id}
            name={params.data.name}
            inviteStatus={params.data.inviteStatus}
          />
        );
      }
    },
    {
      field: 'skills',
      width: 240,
      cellRenderer: (
        params: ICellRendererParams<CandidateProfileTableColumns>
      ) => {
        return <TableV3TruncatedList items={params.value} />;
      },
      serverFilterOptions: {
        searchPlaceholder: 'Search skills',
        selectionType: 'multiple',
        onSearchFilter: async (query: string): Promise<TableV3FilterItem[]> => {
          // No query means the dropdown just opened. Fall back to the tenant's
          // own skills so there is something to pick from before typing.
          const term = query.trim();

          try {
            const filterAnd: SearchableSkillFilterInput[] = [
              { vectorDbTenantName: { eq: vectorDbTenantName } },
              ...(term ? [{ or: buildTextSearchClauses('skill', term) }] : [])
            ];

            const result = await apolloClient.query({
              query: SearchSkillsDocument,
              variables: {
                limit: term ? FILTER_OPTION_LIMIT : UNTYPED_SAMPLE_LIMIT,
                filter: { and: filterAnd },
                sort: [
                  {
                    direction: SearchableSortDirection.Asc,
                    field: term
                      ? SearchableSkillSortableFields.Skill
                      : SearchableSkillSortableFields.Id
                  }
                ]
              }
            });

            const items = extractQueryListItems(result.data);

            const labels = items.map((item) => item.skill);

            const visible = term
              ? labels
              : [...new Set(labels.filter(startsWithLetter))]
                  .sort((a, b) => a.localeCompare(b))
                  .slice(0, FILTER_OPTION_LIMIT);

            return visible.map((label) => ({ label, value: label }));
          } catch (error) {
            logger.error('Failed to fetch skills for filter', { error, query });
            return [];
          }
        }
      }
    },
    {
      field: 'tags',
      width: 240,
      cellRenderer: (
        params: ICellRendererParams<CandidateProfileTableColumns>
      ) => {
        const updateFilterValues = params.context?.updateFilterValues;

        const handleTagClick = (tag: string) => {
          if (updateFilterValues) {
            updateFilterValues({
              column: 'tags',
              item: { label: tag, value: tag }
            });
          }
        };

        return (
          <TableV3TruncatedChipList
            items={params.value}
            onChipClick={handleTagClick}
          />
        );
      },
      serverFilterOptions: {
        searchPlaceholder: 'Search tags',
        selectionType: 'multiple',
        onSearchFilter: async (query: string): Promise<TableV3FilterItem[]> => {
          const term = query.trim();

          try {
            const filterAnd: SearchableTagFilterInput[] = [
              { vectorDbTenantName: { eq: vectorDbTenantName } },
              ...(term ? [{ or: buildTextSearchClauses('tag', term) }] : [])
            ];

            const result = await apolloClient.query({
              query: SearchTagsDocument,
              variables: {
                limit: term ? FILTER_OPTION_LIMIT : UNTYPED_SAMPLE_LIMIT,
                filter: { and: filterAnd }
              }
            });

            const items = extractQueryListItems(result.data);

            const labels = items.map((item) => item.tag);

            const visible = term
              ? labels
              : [...new Set(labels.filter(startsWithLetter))].slice(
                  0,
                  FILTER_OPTION_LIMIT
                );

            return visible.map((label) => ({ label, value: label }));
          } catch (error) {
            logger.error('Failed to fetch tags for filter', { error, query });
            return [];
          }
        }
      }
    },
    {
      field: 'candidateProfileLists',
      headerName: 'Talent lists',
      width: 240,
      hide:
        candidateProfileListProps.type !==
          CandidateProfileListType.CANDIDATES_LIST &&
        candidateProfileListProps.type !==
          CandidateProfileListType.ANALYSIS_CANDIDATES_LIST,
      cellRenderer: (
        params: ICellRendererParams<CandidateProfileTableColumns>
      ) => {
        return (
          <TalentListChipsCell
            talentListIds={params.value ?? []}
            candidateProfileListProps={candidateProfileListProps}
          />
        );
      },
      serverFilterOptions: {
        searchPlaceholder: 'Search talent lists',
        selectionType: 'multiple',
        // Multi-select, so the ticks have to be there to show what is picked.
        // The other columns get this from the filter-mode headers, which this
        // one does not take: talent lists are not a sourcing contract field.
        showCheckboxes: true,
        onSearchFilter: async (query: string): Promise<TableV3FilterItem[]> => {
          // No query means the dropdown just opened. Fall back to the
          // organisation's own lists so there is something to pick from
          // before typing.
          const term = query.trim();

          try {
            const filter = term
              ? JSON.stringify({ or: buildTextSearchClauses('title', term) })
              : undefined;

            const result = await apolloClient.query({
              query: SearchCandidateProfileListsDocument,
              variables: {
                organizationId,
                limit: FILTER_OPTION_LIMIT,
                filter
              }
            });

            const items = extractQueryListItems(result.data);
            // Two lists can share a name, so show the size as well.
            return items.map((item) => ({
              label: item.title,
              subLabel: `${item.candidateProfilesCount} candidate${
                item.candidateProfilesCount !== 1 ? 's' : ''
              }`,
              value: item.id
            }));
          } catch (error) {
            logger.error('Failed to fetch talent lists for filter', {
              error,
              query
            });
            return [];
          }
        }
      }
    },
    {
      field: 'qualifications',
      width: 240,
      cellRenderer: (
        params: ICellRendererParams<CandidateProfileTableColumns>
      ) => {
        return <TableV3TruncatedList maxItems={1} items={params.value} />;
      },
      serverFilterOptions: {
        searchPlaceholder: 'Search qualifications',
        selectionType: 'multiple',
        onSearchFilter: async (query: string): Promise<TableV3FilterItem[]> => {
          const term = query.trim();

          try {
            const filterAnd: SearchableQualificationFilterInput[] = [
              { vectorDbTenantName: { eq: vectorDbTenantName } },
              ...(term
                ? [{ or: buildTextSearchClauses('qualification', term) }]
                : [])
            ];

            const result = await apolloClient.query({
              query: SearchQualificationsDocument,
              variables: {
                limit: term ? FILTER_OPTION_LIMIT : UNTYPED_SAMPLE_LIMIT,
                filter: { and: filterAnd }
              }
            });

            const items = extractQueryListItems(result.data);

            const labels = items.map((item) => item.qualification);

            const visible = term
              ? labels
              : [...new Set(labels.filter(startsWithLetter))].slice(
                  0,
                  FILTER_OPTION_LIMIT
                );

            return visible.map((label) => ({ label, value: label }));
          } catch (error) {
            logger.error('Failed to fetch qualifications for filter', {
              error,
              query
            });
            return [];
          }
        }
      }
    },
    {
      field: 'currentCompany',
      headerName: 'Current employer',
      width: 240,
      searchable: true,
      sortField: 'currentCompany'
    },
    {
      field: 'currentJobTitle',
      headerName: 'Current role',
      width: 240,
      searchable: true,
      sortField: 'currentJobTitle'
    },
    {
      field: 'timeInRole',
      headerName: 'Time in role',
      width: 240,
      sortField: 'timeInRole',
      rangeFilter: YEARS_RANGE_FILTER_SETTINGS
    },
    {
      field: 'yearsOfExperience',
      headerName: 'Years of experience',
      width: 240,
      sortField: 'yearsOfExperience',
      rangeFilter: YEARS_RANGE_FILTER_SETTINGS
    },
    { field: 'location', width: 240, sortField: 'locationName' },
    {
      field: 'email',
      width: 250,
      cellRenderer: (
        params: ICellRendererParams<CandidateProfileTableColumns>
      ) => {
        return (
          <RouterLink to={`mailto:${params.value}`}>{params.value}</RouterLink>
        );
      },
      searchable: true
    },
    {
      field: 'phone',
      width: 200,
      cellRenderer: (
        params: ICellRendererParams<CandidateProfileTableColumns>
      ) => {
        return (
          <RouterLink to={`tel:${params.value}`}>{params.value}</RouterLink>
        );
      },
      searchable: true
    },
    {
      field: 'atsCandidateId',
      headerName: `View in ${integrationProvider}`,
      // An org with no ATS and no candidate linking back to its own system has nothing to
      // put in this column.
      hide: !isAtsConnected && !hasAnyExternalUrl,
      width: 200,
      cellRenderer: (
        params: ICellRendererParams<CandidateProfileTableColumns>
      ) => {
        return (
          <AtsCandidateProfileLink
            candidateProfileId={params.data?.id}
            atsCandidateId={params.data?.atsCandidateId}
            externalUrl={params.data?.externalUrl}
          />
        );
      }
    },
    {
      field: 'originalSource',
      headerName: 'Original source',
      width: 250,
      searchable: true,
      cellRenderer: (
        params: ICellRendererParams<CandidateProfileTableColumns>
      ) => {
        if (!params.value) return null;
        // Add space after commas if missing
        const formattedValue = params.value.replace(/,(?!\s)/g, ', ');
        return <Typography>{formattedValue}</Typography>;
      }
    },
    ...(showRemoveFromList && onRemoveFromList
      ? [
          {
            field: 'id' as const,
            headerName: '',
            width: 60,
            pinned: 'right' as const,
            cellStyle: {
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center'
            },
            cellRenderer: (
              params: ICellRendererParams<CandidateProfileTableColumns>
            ) => {
              if (!params.data) return null;
              const { id } = params.data;
              return (
                <ActionPopover
                  id={id}
                  options={[
                    {
                      label: 'Remove from list',
                      onClick: () => onRemoveFromList(id),
                      itemColor: 'error.main'
                    }
                  ]}
                />
              );
            }
          }
        ]
      : []),
    ...getCustomFieldColumns(customFieldConfigs)
  ];
};
